import { IntegrationAppClient } from "@integration-app/react";
import { DISPLAY_NOTIFICATION, notificationEventBus } from "app/notificationsEventBus";

export type Project = {
  fields: {
    value: string;
    label: string;
  };
};

export type TestCycle = {
  fields: {
    value: string;
    label: string;
  };
};

type testCaseStep = {
  instruction: string;
  expected_result: string;
};

type testExecution = {
  fields: {
    name: string;
    code: string;
    prerequisities: string;
    steps: testCaseStep[];
  };
};

type TestCase = {
  name: string;
  section: string;
  code: string;
  tags: string[];
  prerequisites: string;
  additionalInformation: {
    attachmentRequirements: string[];
    textRequirements: string[];
  };
  fields: {
    instruction: string;
    expectedResult: string;
  }[];
};

export type IntegrationCode = "zephyr-scale" | "zephyr-squad-legacy";

const RECORDS_PER_PAGE = 10;

export const fetchProjects = (
  integrationApp: IntegrationAppClient,
  testCasesSource: string,
  setProjects: (projects: Project[]) => void,
  setIsLoaded: (isLoaded: boolean) => void,
) => {
  const allProjects: Project[] = [];

  const fetchPage = async (cursor: number) => {
    try {
      const response = await integrationApp.connection(testCasesSource).action("get-projects").run({
        cursor: cursor.toString(),
      });
      allProjects.push(...response.output.records);
      setProjects(allProjects);

      if (response.output.cursor) {
        fetchPage(response.output.cursor);
      } else {
        setIsLoaded(true);
      }
    } catch (error) {
      notificationEventBus.dispatch(DISPLAY_NOTIFICATION, {
        type: "danger",
        message: "There was an issue with loading the projects, please try again later.",
      });
      setIsLoaded(true);
    }
  };

  fetchPage(0);
};

export const fetchTestCycles = (
  integrationApp: IntegrationAppClient,
  testCasesSource: string,
  params: { [key: string]: string | undefined },
  setIsLoaded: (isLoaded: boolean) => void,
  setTestCycles: (testCycles: TestCycle[]) => void,
) => {
  const allTestCycles: TestCycle[] = [];

  const fetchPage = async (cursor: number) => {
    try {
      const response = await integrationApp
        .connection(testCasesSource)
        .action("get-test-cycles")
        .run({
          ...params,
          cursor: cursor.toString(),
        });
      allTestCycles.push(...response.output.records);
      setTestCycles(allTestCycles);

      if (response.output.cursor) {
        fetchPage(response.output.cursor);
      } else {
        setIsLoaded(true);
      }
    } catch (error) {
      notificationEventBus.dispatch(DISPLAY_NOTIFICATION, {
        type: "danger",
        message: "There was an issue with loading the test cycles, please try again later.",
      });
      setIsLoaded(true);
    }
  };

  fetchPage(0);
};

const sanitizeHtmlTags = (html: string) =>
  html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/li>/gi, "\n")
    .replace(/(<([^>]+)>)/gi, "");

const mapTestExecutions = (testExecutions: testExecution[]) =>
  testExecutions.map(testExecution => ({
    name: testExecution.fields.name,
    section: "",
    code: testExecution.fields.code,
    tags: [],
    prerequisites: sanitizeHtmlTags(testExecution.fields.prerequisities || ""),
    additionalInformation: {
      attachmentRequirements: [""],
      textRequirements: [""],
    },
    fields: (testExecution.fields?.steps || []).map((step: testCaseStep) => ({
      instruction: sanitizeHtmlTags(step.instruction || ""),
      expectedResult: sanitizeHtmlTags(step.expected_result || ""),
    })),
  }));

export const fetchTestExecutions = (
  integrationApp: IntegrationAppClient,
  testCasesSource: IntegrationCode,
  params: { [key: string]: string | undefined },
  setIsLoaded: (isLoaded: boolean) => void,
  setTestCases: (testCases: TestCase[]) => void,
) => {
  const allTestCases: TestCase[] = [];

  const fetchPage = async (cursor: number) => {
    try {
      const response = await integrationApp
        .connection(testCasesSource)
        .action("get-test-executions")
        .run({
          ...params,
          records_per_page: RECORDS_PER_PAGE.toString(),
          cursor: cursor.toString(),
        });
      const newTestCases = mapTestExecutions(response.output.records);
      allTestCases.push(...newTestCases);
      setTestCases(allTestCases);

      if (response.output.cursor) {
        fetchPage(cursor + RECORDS_PER_PAGE);
      } else {
        setIsLoaded(true);
      }
    } catch (error) {
      notificationEventBus.dispatch(DISPLAY_NOTIFICATION, {
        type: "danger",
        message: "There was an issue with loading the test cases, please try again later.",
      });
      setIsLoaded(true);
    }
  };

  fetchPage(0);
};

export const mapProjects = (projects: Project[]) =>
  projects.map((project: Project) => ({
    value: project.fields.value,
    label: project.fields.label,
  }));

export const mapTestCycles = (testCycles: TestCycle[]) =>
  testCycles.map((testCycle: TestCycle) => ({
    value: testCycle.fields.value,
    label: testCycle.fields.label,
  }));
